import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DOMPurify from 'dompurify';
import {
  Send, FileText, ChevronRight, Database, MessageSquare, Loader2,
  RefreshCw, Download, BookOpen, Search, X, Sparkles, Image, FolderOpen,
  User, Lightbulb, AlertTriangle, DollarSign, ThumbsUp, ThumbsDown,
  TrendingUp, Flame, RotateCcw, CheckCircle, Eye, Users as UsersIcon, Building2
} from 'lucide-react';
import KBIcon from './KBIcon';
import { useKB } from './KBContext';
import { SUGGESTED_QUESTIONS, DISCOVER_QUESTIONS, DOC_CARDS_LIMIT } from './constants';
import MemberSheet, { MemberAvatar } from './MemberSheet';
import OrgSheet from './OrgSheet';

const ChatPanel = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [showOrg, setShowOrg] = useState(false);
  const {
    messages, setMessages, inputValue, setInputValue, isQuerying,
    sessionId, setSessionId, handleQuery, sendQuickQuestion,
    ingestionRunning, documents, isIngesting, handleSyncNew, memberInfo,
    hotPicks, hotPicksIdx, refreshHotPicks, trendingData,
    feedbackGiven, handleFeedback,
    expandedDocCards, setExpandedDocCards,
    setShowDisclaimerModal,
    showSuggestionBox, setShowSuggestionBox,
    feedbackType, setFeedbackType,
    suggestionText, setSuggestionText,
    handleSuggestion, sendingSuggestion, suggestionSent,
    handleDownload, handleView, relatedQueries,
    setActiveTab, setSelectedDoc,
    chatEndRef, retryLastQuery
  } = useKB();

  const suggestionBoxRef = useRef(null);

  // Error messages get a friendly "Try again" affordance instead of a dead end.
  const ERROR_PREFIXES = ['Connection error', 'Something went wrong', 'The server is', 'Oops', 'The server briefly', "That didn't", 'Still having trouble'];
  const isErrorMsg = (c) => !!c && ERROR_PREFIXES.some((p) => c.startsWith(p));

  // Render quick_actions returned by the backend (greetings / off-topic redirects)
  const handleQuickAction = (action) => {
    if (!action || !action.action) return;
    if (action.action === 'switch_tab') {
      setActiveTab(action.value);
    } else if (action.action === 'send_message') {
      sendQuickQuestion(action.value);
    } else if (action.action === 'open_org') {
      setShowOrg(true);
    } else if (action.action === 'open_feedback') {
      setFeedbackType(action.value === 'suggestion' ? 'suggestion' : 'report');
      setShowSuggestionBox(true);
      setTimeout(() => suggestionBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
    }
  };
  const QuickActionIcon = ({ name }) => {
    if (name === 'documents') return <FolderOpen size={14} />;
    if (name === 'members') return <UsersIcon size={14} />;
    if (name === 'org') return <Building2 size={14} />;
    if (name === 'trending') return <TrendingUp size={14} />;
    if (name === 'topic') return <Sparkles size={14} />;
    if (name === 'report') return <AlertTriangle size={14} />;
    return <ChevronRight size={14} />;
  };

  return (
    <div className="kb-chat-container" data-testid="kb-chat-container">
      {ingestionRunning && (
        <div className="kb-chat-ingestion-notice" data-testid="kb-chat-ingestion-notice">
          <Loader2 size={14} className="kb-spin" />
          <span>Documents are being ingested in the background. Chat is available but may respond slower than usual.</span>
        </div>
      )}
      <div className="kb-chat-messages">
        {messages.length === 0 && (
          <div className="kb-chat-welcome">
            <KBIcon size={52} className="kb-welcome-icon" />
            <h1 className="kb-welcome-title">IPMI Document Intelligence</h1>
            <h2>Ask anything about IPMI documents</h2>
            {documents.length === 0 && !isIngesting && !memberInfo && (
              <div className="kb-first-sync-banner" data-testid="kb-first-sync-banner">
                <Database size={20} />
                <div>
                  <strong>No documents loaded yet</strong>
                  <p>Sync your R2 documents to get started</p>
                </div>
                <button className="kb-first-sync-btn" onClick={handleSyncNew} disabled={isIngesting}
                  data-testid="kb-first-sync-btn">
                  <RefreshCw size={16} /> Sync Documents
                </button>
              </div>
            )}
            <div className="kb-suggested-questions" data-testid="kb-suggested-questions">
              {SUGGESTED_QUESTIONS.map((sq, idx) => (
                <button key={idx} className="kb-suggested-btn"
                  onClick={() => sendQuickQuestion(sq.question)}
                  data-testid={`kb-suggested-${idx}`}>
                  <sq.icon size={16} />
                  <span>{sq.label}</span>
                  <ChevronRight size={14} />
                </button>
              ))}
              <button className="kb-suggested-btn" onClick={() => setActiveTab('documents')}
                data-testid="kb-quick-browse-docs">
                <FolderOpen size={16} />
                <span>Browse document library</span>
                <ChevronRight size={14} />
              </button>
              <button className="kb-suggested-btn"
                onClick={() => sendQuickQuestion("Who are the key members and contributors in IPMI?")}
                data-testid="kb-quick-find-member">
                <User size={16} />
                <span>Find a member or contributor</span>
                <ChevronRight size={14} />
              </button>
            </div>

            {/* People & governance discovery chips */}
            <div className="kb-suggested-questions" data-testid="kb-discover-questions" style={{ marginTop: 4 }}>
              {DISCOVER_QUESTIONS.map((dq, idx) => (
                <button key={idx} className="kb-suggested-btn"
                  onClick={() => sendQuickQuestion(dq.question)}
                  data-testid={`kb-discover-${idx}`}>
                  <dq.icon size={16} />
                  <span>{dq.label}</span>
                  <ChevronRight size={14} />
                </button>
              ))}
            </div>

            {/* Hot Picks */}
            {hotPicks.length > 0 && (() => {
              const currentSet = hotPicks[hotPicksIdx % hotPicks.length];
              return (
                <div className="kb-hotpicks-section" data-testid="kb-hotpicks">
                  <div className="kb-hotpicks-header">
                    <Flame size={16} />
                    <span>Hot Picks: {currentSet.name}</span>
                    <button className="kb-hotpicks-refresh" onClick={refreshHotPicks}
                      data-testid="kb-hotpicks-refresh" title="See different picks">
                      <RefreshCw size={14} />
                    </button>
                    <span className="kb-hotpicks-counter">
                      {(hotPicksIdx % hotPicks.length) + 1}/{hotPicks.length}
                    </span>
                  </div>
                  <div className="kb-hotpicks-grid">
                    {currentSet.documents.slice(0, 4).map((doc, i) => (
                      <div key={i} className="kb-hotpick-card"
                        onClick={() => { setActiveTab('documents'); setSelectedDoc(doc); }}
                        data-testid={`kb-hotpick-${i}`}>
                        <div className="kb-hotpick-type">
                          {doc.is_website ? <BookOpen size={12} /> : <FileText size={12} />}
                          <span>{doc.file_type?.toUpperCase()}</span>
                          {doc.year && <span>{doc.year}</span>}
                        </div>
                        <span className="kb-hotpick-title">{doc.title}</span>
                        {doc.category && <span className="kb-hotpick-cat">{doc.category}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Trending Topics */}
            {trendingData?.trending_tags?.length > 0 && (
              <div className="kb-trending-section" data-testid="kb-trending">
                <div className="kb-trending-header">
                  <TrendingUp size={16} />
                  <span>Trending Topics</span>
                </div>
                <div className="kb-trending-tags">
                  {trendingData.trending_tags.slice(0, 6).map((t, i) => (
                    <button key={i} className="kb-trending-tag"
                      onClick={() => sendQuickQuestion(`Tell me about ${t.tag} in the context of precious metals`)}
                      data-testid={`kb-trending-tag-${i}`}>
                      {t.tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="kb-feedback-toggles" data-testid="kb-feedback-toggles">
              <button className={`kb-feedback-toggle ${showSuggestionBox && feedbackType === 'suggestion' ? 'open' : ''}`}
                onClick={() => { setFeedbackType('suggestion'); setShowSuggestionBox(feedbackType === 'suggestion' ? !showSuggestionBox : true); }}
                data-testid="kb-feedback-toggle">
                <Lightbulb size={14} />
                Have a suggestion?
              </button>
              <button className={`kb-feedback-toggle report ${showSuggestionBox && feedbackType === 'report' ? 'open' : ''}`}
                onClick={() => { setFeedbackType('report'); setShowSuggestionBox(feedbackType === 'report' ? !showSuggestionBox : true); }}
                data-testid="kb-report-toggle">
                <AlertTriangle size={14} />
                Report a problem
              </button>
            </div>
            <AnimatePresence>
              {showSuggestionBox && (
                <motion.div className="kb-suggestion-box" ref={suggestionBoxRef}
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }} data-testid="kb-suggestion-box">
                  <form className="kb-suggestion-form" onSubmit={handleSuggestion}>
                    <input type="text" value={suggestionText}
                      onChange={(e) => setSuggestionText(e.target.value)}
                      placeholder={feedbackType === 'report' ? "Describe the issue you're experiencing..." : "E.g. Add more papers about PGM recycling..."}
                      className="kb-suggestion-input" data-testid="kb-suggestion-input" autoFocus />
                    <button type="submit" className={`kb-suggestion-submit ${feedbackType === 'report' ? 'report' : ''}`}
                      disabled={!suggestionText.trim() || sendingSuggestion}
                      data-testid="kb-suggestion-submit">
                      {sendingSuggestion ? 'Sending...' : 'Submit'}
                    </button>
                  </form>
                  {suggestionSent && (
                    <div className="kb-suggestion-success" data-testid="kb-suggestion-success">
                      <CheckCircle size={14} /> Thank you! Your suggestions and reports are an important part of improving the platform.
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
            <button className="kb-ai-disclaimer-link welcome" onClick={() => setShowDisclaimerModal(true)} data-testid="kb-ai-disclaimer-welcome">
              <AlertTriangle size={11} /> AI Disclaimer
            </button>
          </div>
        )}

        {messages.map((msg, idx) => (
          <motion.div key={idx} className={`kb-message ${msg.role}`}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            data-testid={`kb-message-${idx}`}>
            <div className="kb-message-content">
              {msg.role === 'assistant' ? (
                <div className="kb-message-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>').replace(/\[(\d+)\]/g, (match, num) => {
                  return `<a class="kb-excerpt-ref" href="#kb-source-${idx}-${parseInt(num)-1}" onclick="event.preventDefault();document.getElementById('kb-source-${idx}-${parseInt(num)-1}')?.scrollIntoView({behavior:'smooth',block:'center'})">[${num}]</a>`;
                }), { ALLOWED_TAGS: ['br', 'a', 'strong', 'em', 'code', 'b', 'i'], ALLOWED_ATTR: ['class', 'href', 'id', 'onclick'] }) }} />
              ) : (
                <div className="kb-message-text">{msg.content}</div>
              )}
            </div>
            {msg.role === 'assistant' && isErrorMsg(msg.content) && (
              <button
                type="button"
                onClick={retryLastQuery}
                disabled={isQuerying}
                data-testid={`kb-chat-retry-${idx}`}
                style={{
                  marginTop: 10,
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '8px 16px',
                  background: 'rgba(37,99,235,0.08)',
                  border: '1px solid rgba(37,99,235,0.30)',
                  borderRadius: 999,
                  color: 'var(--kb-accent, #2563EB)',
                  fontSize: 13, fontWeight: 600,
                  cursor: isQuerying ? 'default' : 'pointer',
                  opacity: isQuerying ? 0.6 : 1,
                  transition: 'background 0.18s ease, border-color 0.18s ease',
                }}>
                <RefreshCw size={14} className={isQuerying ? 'kb-spin' : ''} /> Try again
              </button>
            )}
            {msg.sources?.length > 0 && (
              <div className="kb-sources">
                <span className="kb-sources-label">Sources:</span>
                {msg.sources.map((src, sIdx) => (
                  <button key={sIdx} className="kb-source-chip" id={`kb-source-${idx}-${sIdx}`}
                    onClick={() => handleView(src.r2_key)} data-testid={`kb-source-${sIdx}`}>
                    <span className="kb-source-num">{sIdx + 1}</span>
                    <FileText size={12} /> {src.title}
                  </button>
                ))}
              </div>
            )}
            {msg.matched_documents?.length > 0 && (() => {
              const allDocs = msg.matched_documents;
              const isExpanded = expandedDocCards[idx];
              const visibleDocs = isExpanded ? allDocs : allDocs.slice(0, DOC_CARDS_LIMIT);
              const hasMore = allDocs.length > DOC_CARDS_LIMIT;
              return (
                <div className="kb-matched-docs" data-testid="kb-matched-docs">
                  <span className="kb-matched-docs-label">
                    <Search size={13} /> {allDocs.length} matching document{allDocs.length !== 1 ? 's' : ''} found:
                  </span>
                  <div className="kb-matched-docs-grid">
                    {visibleDocs.map((doc, dIdx) => (
                      <div key={dIdx} className="kb-matched-doc-card"
                        onClick={() => { setActiveTab('documents'); setSelectedDoc(doc); }}
                        data-testid={`kb-matched-doc-${dIdx}`}>
                        <div className="kb-matched-doc-header">
                          {doc.is_website ? <BookOpen size={14} /> : doc.is_asset ? <Image size={14} /> : <FileText size={14} />}
                          <span className="kb-matched-doc-type">{doc.file_type?.toUpperCase()}</span>
                          {doc.year && <span className="kb-matched-doc-year">{doc.year}</span>}
                        </div>
                        <span className="kb-matched-doc-title">{doc.title}</span>
                        {doc.category && <span className="kb-matched-doc-cat">{doc.category}</span>}
                        <div className="kb-matched-doc-actions">
                          <button className="kb-matched-doc-dl" onClick={(e) => { e.stopPropagation(); handleView(doc.r2_key); }}>
                            <Eye size={12} /> View
                          </button>
                          {!doc.is_asset && !doc.is_website && (
                            <button className="kb-matched-doc-ask" onClick={(e) => { e.stopPropagation(); sendQuickQuestion(`Give me a detailed summary of "${doc.title}"`); }}>
                              <Sparkles size={12} /> Summarize
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {hasMore && !isExpanded && (
                    <button className="kb-matched-docs-show-more" data-testid="kb-show-more-docs"
                      onClick={() => setExpandedDocCards(prev => ({ ...prev, [idx]: true }))}>
                      Show more ({DOC_CARDS_LIMIT}/{allDocs.length})
                    </button>
                  )}
                  {hasMore && isExpanded && (
                    <button className="kb-matched-docs-show-more" data-testid="kb-show-less-docs"
                      onClick={() => setExpandedDocCards(prev => ({ ...prev, [idx]: false }))}>
                      Show less
                    </button>
                  )}
                </div>
              );
            })()}
            {msg.matched_members?.length > 0 && (
              <div className="kb-matched-members" data-testid="kb-matched-members">
                <span className="kb-matched-members-label">
                  <UsersIcon size={13} /> {msg.matched_members.length} IPMI member{msg.matched_members.length !== 1 ? 's' : ''} you may want to contact:
                </span>
                <div className="kb-matched-members-grid">
                  {msg.matched_members.map((m, mIdx) => (
                    <button
                      key={`${m.member_id}-${mIdx}`}
                      className="kb-matched-member-chip"
                      onClick={() => setSelectedMember(m)}
                      data-testid={`kb-matched-member-${m.member_id}`}
                    >
                      <MemberAvatar member={m} size={36} />
                      <div className="kb-matched-member-text">
                        <span className="kb-matched-member-name">{m.full_name}</span>
                        <span className="kb-matched-member-role">
                          {[m.position_title, m.company_name].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msg.quick_actions?.length > 0 && (
              <div className="kb-quick-actions-row" data-testid="kb-quick-actions"
                style={{
                  marginTop: 14, marginBottom: 4,
                  display: 'flex', flexWrap: 'wrap', gap: 8,
                }}>
                {msg.quick_actions.map((qa, qIdx) => (
                  <button
                    key={qIdx}
                    type="button"
                    onClick={() => handleQuickAction(qa)}
                    data-testid={`kb-quick-action-${qIdx}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7,
                      padding: '8px 14px',
                      background: 'rgba(91,154,255,0.08)',
                      border: '1px solid rgba(91,154,255,0.28)',
                      borderRadius: 999,
                      color: 'var(--kb-accent, #2563EB)',
                      fontSize: 13, fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.18s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(91,154,255,0.16)';
                      e.currentTarget.style.borderColor = 'rgba(91,154,255,0.55)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(91,154,255,0.08)';
                      e.currentTarget.style.borderColor = 'rgba(91,154,255,0.28)';
                    }}
                  >
                    <QuickActionIcon name={qa.icon} />
                    <span>{qa.label}</span>
                  </button>
                ))}
              </div>
            )}
            {msg.role === 'assistant' && msg.content && !msg.content.startsWith('Connection error') && !msg.content.startsWith('Something went wrong') && !msg.content.startsWith('The server is') && (
              <div className="kb-feedback-row" data-testid={`kb-feedback-${idx}`}>
                <span className="kb-feedback-label">Was this helpful?</span>
                <button className={`kb-feedback-btn${feedbackGiven[idx] === 'up' ? ' active' : ''}`}
                  onClick={() => handleFeedback(idx, 'up')} disabled={!!feedbackGiven[idx]}
                  data-testid={`kb-feedback-up-${idx}`}>
                  <ThumbsUp size={14} />
                </button>
                <button className={`kb-feedback-btn${feedbackGiven[idx] === 'down' ? ' active' : ''}`}
                  onClick={() => handleFeedback(idx, 'down')} disabled={!!feedbackGiven[idx]}
                  data-testid={`kb-feedback-down-${idx}`}>
                  <ThumbsDown size={14} />
                </button>
                {feedbackGiven[idx] && <span className="kb-feedback-thanks">Thanks for your feedback!</span>}
                <button className="kb-ai-disclaimer-link" onClick={() => setShowDisclaimerModal(true)} data-testid="kb-ai-disclaimer">
                  <AlertTriangle size={11} /> AI Disclaimer
                </button>
              </div>
            )}
            {relatedQueries[idx]?.length > 0 && (
              <div className="kb-related-queries" data-testid={`kb-related-${idx}`}>
                <span className="kb-related-label">Others also asked:</span>
                {relatedQueries[idx].map((rq, rIdx) => (
                  <button key={rIdx} className="kb-related-btn"
                    onClick={() => sendQuickQuestion(rq.question)}
                    data-testid={`kb-related-q-${rIdx}`}>
                    <MessageSquare size={12} />
                    <span>{rq.question}</span>
                    <ChevronRight size={12} />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        ))}

        {isQuerying && (
          <motion.div className="kb-message assistant" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="kb-message-content">
              <div className="kb-typing"><Loader2 size={16} className="kb-spin" /><span>Searching documents...</span></div>
            </div>
          </motion.div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* In-thread feedback / report box (opened from a chat quick-action) */}
      <AnimatePresence>
        {messages.length > 0 && showSuggestionBox && (
          <motion.div className="kb-suggestion-box kb-suggestion-box-inline" ref={suggestionBoxRef}
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} data-testid="kb-suggestion-box-inline"
            style={{ margin: '0 auto 10px', maxWidth: 760, width: '100%' }}>
            <form className="kb-suggestion-form" onSubmit={handleSuggestion}>
              <input type="text" value={suggestionText}
                onChange={(e) => setSuggestionText(e.target.value)}
                placeholder={feedbackType === 'report' ? "Tell us what's missing or wasn't found..." : "Suggest a document or topic to add..."}
                className="kb-suggestion-input" data-testid="kb-suggestion-input-inline" autoFocus />
              <button type="submit" className={`kb-suggestion-submit ${feedbackType === 'report' ? 'report' : ''}`}
                disabled={!suggestionText.trim() || sendingSuggestion}
                data-testid="kb-suggestion-submit-inline">
                {sendingSuggestion ? 'Sending...' : 'Submit'}
              </button>
              <button type="button" className="kb-suggestion-submit" onClick={() => setShowSuggestionBox(false)}
                style={{ background: 'transparent', color: 'var(--kb-fg-soft, #8B96A5)' }}
                data-testid="kb-suggestion-close-inline">
                <X size={14} />
              </button>
            </form>
            {suggestionSent && (
              <div className="kb-suggestion-success" data-testid="kb-suggestion-success-inline">
                <CheckCircle size={14} /> Thank you! Your note helps us improve the library.
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleQuery} className="kb-chat-input-form" data-testid="kb-chat-form">
        {messages.length > 0 && (
          <button type="button" className="kb-chat-clear-btn" data-testid="kb-chat-clear"
            onClick={() => { setMessages([]); setSessionId(null); }}
            title="New conversation">
            <RotateCcw size={16} />
          </button>
        )}
        <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)}
          placeholder="Ask a question about IPMI documents..." className="kb-chat-input"
          disabled={isQuerying} data-testid="kb-chat-input" />
        <button type="submit" className="kb-chat-send-btn" disabled={!inputValue.trim() || isQuerying}
          data-testid="kb-chat-send"><Send size={18} /></button>
      </form>

      {selectedMember && (
        <MemberSheet member={selectedMember} onClose={() => setSelectedMember(null)} />
      )}
      {showOrg && (
        <OrgSheet
          onClose={() => setShowOrg(false)}
          onMemberClick={(mid) => { setShowOrg(false); setSelectedMember({ member_id: mid }); }}
        />
      )}
    </div>
  );
};

export default ChatPanel;
